import { generateKeyPair, CompactEncrypt, compactDecrypt, importSPKI, importX509, exportSPKI } from 'jose';
import type { KeyLike } from 'jose';
import { Message, WPPError, MessageOutput } from './common-objects';
import type {
    EntrustWPPIssuerConfig,
    EntrustWPPConfig,
    OnResult,
    OnFailure,
    OnCancel,
    GetPushOpaqueData,
} from './common-objects';
import { RequestUtil } from '../common';
import { loadScriptByName } from './script-loader';

export abstract class AbstractEntrustWebPushProvisionning {
    config: EntrustWPPConfig;
    issuerConfig: EntrustWPPIssuerConfig;
    privateKey: KeyLike;
    publicKey: KeyLike;
    keyPairKid: string;
    buttonId: string;
    onResult: OnResult;
    onFailure: OnFailure;
    onCancel?: OnCancel;
    getPushOpaqueDataPromise: GetPushOpaqueData;

    OPAQUE_DATA_VERSION = '04';
    JWE_ENC = 'A256GCM';
    JWE_ALG = 'ECDH-ES+A256KW';

    dcsKid: string;
    dcsPublicKeyPem: string;
    dcsPublicKey: KeyLike;

    constructor(
        config: EntrustWPPConfig,
        issuerConfig: EntrustWPPIssuerConfig,
        getPushOpaqueDataPromise: GetPushOpaqueData,
        onResult: OnResult,
        onFailure: OnResult,
        onCancel?: OnResult
    ) {
        this.config = config;
        this.issuerConfig = issuerConfig;
        this.dcsKid = issuerConfig.dcsKid;
        this.onResult = onResult;
        this.onFailure = onFailure;
        this.onCancel = onCancel;
        this.getPushOpaqueDataPromise = getPushOpaqueDataPromise;
    }

    /**
     * Get the user's locale from config or browser
     * @returns The locale code (e.g., 'fr-FR', 'en-US')
     */
    protected getLocale(): string {
        if (this.config.locale) {
            return this.config.locale;
        }
        return navigator.language || (navigator as any).userLanguage || 'en';
    }

    /**
     * Initializes the instance asynchronously.
     * Must be called after construction.
     */
    async init(): Promise<void> {
        try {
            await loadScriptByName(this.getScriptName()); // Load the specific script based on the wallet (google or apple)
            // Load DCS certificate and import as crypto key
            this.dcsPublicKeyPem = await this.getDCSCert(this.dcsKid);
            this.dcsPublicKey = await this.importCertAsCryptoKey(this.dcsPublicKeyPem, this.JWE_ALG);

            // Generate key pair
            await this.generateKP();

            // Handle button creation
            this.handleButton();

            // Call library specific initialization if needed
            this.postInit();
        } catch (error) {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Failed to initialize Web Push Provisionning'));
            throw error;
        }
    }

    abstract postInit(): void; // Abstract function to handle specific initialization for Google and Apple if needed

    abstract generateButton(): void; // abstract function to generate the button into the DOM

    abstract getScriptName(): string; // abstract that returns the script name to load based on the wallet (google or apple)

    abstract getWalletName(): string; // Abstract that returns the walletName used in the walletOpaqueData

    abstract completeWalletOpaqueData(payload?: any): any; // Abstract that returns the walletOpaqueData based on Google and Apple

    handleSuccess(msge: MessageOutput): void {
        this.onResult(msge);
    }
    handleErrors(msge: MessageOutput, statusMessage: any): void {
        this.onFailure(msge);
    }
    handleCancel(msge: MessageOutput): void {
        this.onCancel?.(msge);
    }

    // Function that generates the EC P-256 key pair for ECDH-ES key agreement.
    // The public key will be the webPuk and its id will be the webPukId of the wallet opaque data.
    async generateKP() {
        // Generate a unique KID for this key pair
        this.keyPairKid = this.generateKid();

        // Generate an ECDH-ES P-256 key pair (algorithm.name === 'ECDH'),
        // required to decrypt JWE responses using alg "ECDH-ES".
        const ecdhKey = await generateKeyPair('ECDH-ES', {
            crv: 'P-256',
            extractable: true,
        });
        this.privateKey = ecdhKey.privateKey;
        this.publicKey = ecdhKey.publicKey;
    }

    // Function that generates a unique Key ID (KID), max 32 characters
    private generateKid(): string {
        // Preferred: 16 random bytes as hex (32 chars). Equivalent entropy to a UUIDv4.
        if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
            const bytes = new Uint8Array(16);
            crypto.getRandomValues(bytes);
            return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        }
        // Fallback: timestamp + random string, truncated to 32 chars
        const timestamp = Date.now().toString(32);
        const randomStr = Math.random().toString(32).substring(2, 15);
        return `${timestamp}-${randomStr}`.substring(0, 32);
    }

    // Function that handle button creation if needed
    handleButton(): void {
        if (typeof this.config?.target === 'string') {
            this.buttonId = this.config.target;
        } else {
            this.buttonId = `add-to-${this.getWalletName()}-wallet`;
            // call specific function to generate the button in the DOM
            this.generateButton();
        }
    }

    /**
     * Retrieve WebPush public key based on the provided key id.
     * WebPush public keys are stored on the same host of the web-push-provisioning script but in a different folder.
     * WebPush public key location <scriptHost>/sec/certs/<kid>.pub
     * @param {string} kid The WebPush key id
     * @returns {Promise<string>} The public key as PEM text
     */
    async getDCSPublicKey(kid: string): Promise<string> {
        try {
            const response = await RequestUtil.getResource('web-push-provisioning.js', `certs/${kid}.pub`, 'data-wpp');
            return response.text();
        } catch (error) {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Unable to retrieve WebPush public key'));
            throw error;
        }
    }

    /**
     * Retrieve WebPush public key based on the provided key id.
     * WebPush public keys are stored on the same host of the web-push-provisioning script but in a different folder.
     * WebPush public key location <scriptHost>/sec/certs/<kid>.pub
     * @param {string} kid The WebPush key id
     * @returns {Promise<string>} The public key as PEM text
     */
    async getDCSCert(kid: string): Promise<string> {
        try {
            const response = await RequestUtil.getResource('web-push-provisioning.js', `certs/${kid}.crt`, 'data-wpp');
            return response.text();
        } catch (error) {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Unable to retrieve WebPush certificate'));
            throw error;
        }
    }

    // Function that returns the wallet opaque data that will be passed to the function returning a promise
    async generateWalletOpaqueData(payload?: any): Promise<string> {
        const clearOpaqueData = {
            walletName: this.getWalletName(),
            webPuk: await this.exportPublicKeyAsPEM(this.publicKey), // public key directement sans le begin - end-certificate
            webPukId: this.keyPairKid,
            issuerPushSessionId: this.config.issuerPushSessionId,
            ...this.completeWalletOpaqueData(payload),
        };
        const encryptedData = await this.encryptWalletOpaqueData(clearOpaqueData);
        return this.OPAQUE_DATA_VERSION + encryptedData;
    }

    // Function that encrypts the opaque data with the backend certificate (key WEBPUSH_JWE_KP)
    encryptWalletOpaqueData(content: any): Promise<string> {
        const payload = typeof content === 'string' ? content : JSON.stringify(content);
        return new CompactEncrypt(new TextEncoder().encode(payload))
            .setProtectedHeader({
                enc: this.JWE_ENC,
                alg: this.JWE_ALG,
                kid: this.dcsKid,
                iat: Date.now(),
                aud: this.issuerConfig.environment === 'production' ? 'prod' : 'test',
            })
            .encrypt(this.dcsPublicKey);
    }

    // Function that decrypts the pushOpaque data with the private key generated in generateKP
    decryptPushOpaqueData(content: string) {
        return compactDecrypt(content, this.privateKey);
    }

    private async importCertAsCryptoKey(x509: string, alg: string): Promise<KeyLike> {
        try {
            const cert = await importX509(x509, alg, { extractable: true }).catch(() => {
                this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Failed to import DCS certificate as CryptoKey'));
            });
            return cert as KeyLike;
        } catch (error) {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Failed to import DCS certificate as CryptoKey'));
            throw error;
        }
    }

    /**
     * Convert the public key to a base64url-encoded SPKI (no PEM header/footer, no padding).
     * @param {CryptoKey} publicKey
     * @returns {Promise<string>} The SPKI public key encoded as base64url
     */
    async exportPublicKeyAsPEM(publicKey) {
        /** PUBLIC KEY : The exported key is then PEM-encoded */
        const spkiPem = (await exportSPKI(publicKey).catch((error) => {
            this.onFailure(new WPPError(Message.TECHNICAL_ERROR, 'Failed to import DCS public key as CryptoKey'));
            throw error;
        })) as string;
        // Strip PEM header/footer and whitespace to get standard base64 of the SPKI DER
        const base64 = spkiPem.replace(/-----(BEGIN|END) PUBLIC KEY-----|\s+/g, '');
        // Convert to base64url: + -> -, / -> _, strip '=' padding
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    }
}
